import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Loader2, MessageCircle } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { submitContact } from "@/api";

const CARDS = [
  { icon: Phone, title: "Call us", lines: [["010 534 9926", "tel:+27105349926"], ["061 450 7416", "tel:+27614507416"]], testid: "contact-card-phone" },
  { icon: Mail, title: "Email us", lines: [["admin@compcarmotors.co.za", "mailto:admin@compcarmotors.co.za"]], testid: "contact-card-email" },
  { icon: MapPin, title: "Visit us", lines: [["Office 10A, 136 North Rand Road", null], ["Jansen Park, Boksburg", null]], testid: "contact-card-address" },
  { icon: Clock, title: "Hours", lines: [["Mon–Fri 08:00–17:00", null], ["Sat 08:00–13:00", null]], testid: "contact-card-hours" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Message sent — we'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Could not send. Please call 010 534 9926 instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 pb-24 min-h-screen">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-sky-300 font-bold mb-4">Talk to a human</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-black text-white mb-4">
            We're in Boksburg. Come say hello.
          </h1>
          <p className="text-base text-slate-400 max-w-2xl mb-14">
            Questions about a vehicle, your application or how rent-to-own
            works? Reach us on any channel below.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {CARDS.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div data-testid={c.testid} className="bg-[#122659] border border-sky-300/20 rounded-xl p-8 h-full hover:border-[#F57C00]/50 transition-colors duration-300">
                <c.icon size={26} className="text-[#F57C00] mb-5" />
                <h3 className="font-display text-lg font-bold text-white mb-3">{c.title}</h3>
                {c.lines.map(([text, href]) =>
                  href ? (
                    <a key={text} href={href} className="block text-sm text-slate-300 hover:text-white transition-colors">{text}</a>
                  ) : (
                    <p key={text} className="text-sm text-slate-400">{text}</p>
                  )
                )}
              </div>
            </Reveal>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <Reveal>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="bg-[#122659]/80 backdrop-blur-xl border border-sky-300/20 rounded-xl p-8 md:p-10 space-y-5"
            >
              <h2 className="font-display text-2xl font-bold text-white mb-2">Send us a message</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="input-label" htmlFor="ct-name">Name</label>
                  <input id="ct-name" data-testid="contact-input-name" required className="input-field" value={form.name} onChange={set("name")} placeholder="Your name" />
                </div>
                <div>
                  <label className="input-label" htmlFor="ct-phone">Phone</label>
                  <input id="ct-phone" data-testid="contact-input-phone" className="input-field" value={form.phone} onChange={set("phone")} placeholder="082 000 0000" />
                </div>
              </div>
              <div>
                <label className="input-label" htmlFor="ct-email">Email</label>
                <input id="ct-email" data-testid="contact-input-email" type="email" required className="input-field" value={form.email} onChange={set("email")} placeholder="you@example.co.za" />
              </div>
              <div>
                <label className="input-label" htmlFor="ct-message">Message</label>
                <textarea id="ct-message" data-testid="contact-input-message" required rows={5} className="input-field resize-none" value={form.message} onChange={set("message")} placeholder="How can we help?" />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={loading}
                className="w-full bg-[#F57C00] hover:bg-[#E67300] disabled:opacity-60 text-white font-bold px-6 py-4 rounded-md transition-colors duration-300 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 size={18} className="animate-spin" />}
                {loading ? "Sending…" : "Send message"}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="h-full min-h-[320px] rounded-xl overflow-hidden border border-sky-300/20 relative">
              <iframe
                title="CompCar Motors location"
                data-testid="contact-map"
                src="https://www.google.com/maps?q=136+North+Rand+Road,+Jansen+Park,+Boksburg&output=embed"
                className="w-full h-full min-h-[320px] grayscale invert-[0.9] contrast-[0.9]"
                loading="lazy"
              />
              <a
                href="https://wa.me/27614507416"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="absolute bottom-5 right-5 inline-flex items-center gap-2 bg-[#F57C00] hover:bg-[#E67300] text-white text-sm font-bold px-5 py-3 rounded-md shadow-xl transition-colors duration-300"
              >
                <MessageCircle size={18} /> WhatsApp us
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
